import React, { useState, useRef } from "react";
import { useSelector } from "react-redux";
import store, { RootState } from "../store";
import { SAVE_QUOTE_RESPONSE, SaveQuoteRequest } from "../api/types";
import { useApi } from "../api";
import { v4 as uuidv4 } from 'uuid';
import {
  lobToBundleCardInfo,
  ProductCardInfo,
} from "../pages/agent-partner-select/agent-partner-select";
import { usePageLoadingContext } from "../contexts/PageLoadingContext";
import { updateQuote } from "../store/quote/action";
import { API_ERROR_MESSAGE } from "../api/launchpad-api";
import { getBundleCardColors } from "../config/partner-config/agent-partner-select-constants";
import { getRedirectMessageAvailableCarriers } from "../config/quote-outcome/redirectAlertMessages";
import { getBundlePartnerLayout, isRedirectControl, isSecondPrimaryPill } from "../utils/vendor-filter";

interface AgentPartnerProductBundleProps {
  cardInfo?: lobToBundleCardInfo;
  showProducts: boolean;
  setShowAlert?: (show: boolean) => void;
  setAlertMessage?: (message: { title: string; message: string }) => void;
  waitForUserConfirmation?: (partnerInfo: ProductCardInfo) => Promise<void>;
}

export const AgentPartnerBundleCard: React.FC<
  AgentPartnerProductBundleProps
> = ({
  cardInfo,
  showProducts = true,
  setShowAlert = () => { },
  setAlertMessage = () => { },
  waitForUserConfirmation = () => { },
}) => {
    const {
      mainBundleCardColor,
      mainProductCardColor,
      backgroundBundleCardColor,
      disabledTextColor,
      disabledButtonTextColor,
      disabledPartnerBackgroundButtonColor,
      copyIconColor,
    } = getBundleCardColors(showProducts);

    const api = useApi();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const quote = useSelector((state: RootState) => state.quote.quote);
    const { showLoader, hideLoader } = usePageLoadingContext();
    const previousBusinessKeywordId = useRef<string | null>(null);

    const toggleDropdown = () => {
      setIsDropdownOpen((prev) => !prev);
    };

    const truncateBusinessClass = (maxChars: number, businessClass?: string) => {
      if (!businessClass) return "";
      return businessClass.length > maxChars
        ? businessClass.slice(0, maxChars - 4) + " ..."
        : businessClass;
    };

    const handleButtonClick = async (
      partnerInfo: ProductCardInfo,
      isFirstLob: boolean
    ) => {
      if (partnerInfo.isRedirectable === false) return;
      if (getRedirectMessageAvailableCarriers().includes(partnerInfo.carrier)) {
        try {
          await waitForUserConfirmation(partnerInfo);
        } catch (error) {
          return;
        }
      }
      showLoader();
      let saveQuote: SaveQuoteRequest = {
        ...quote.data,
        CarrierDetail:
          partnerInfo.carrier !== "Geico"
            ? [
              {
                CarrierCob: partnerInfo.businessClass,
                CarrierCode: partnerInfo.carrier,
                CarrierName: partnerInfo.carrier?.toUpperCase(),
              },
            ]
            : null,
        SelectedProducts: isFirstLob
          ? cardInfo?.cardInfo.firstLobSelectedProducts ?? []
          : cardInfo?.cardInfo.secondLobSelectedProducts ?? [],
      };

      if (
        (quote.data.SelectedProducts[0]?.LobCode !==
          saveQuote.SelectedProducts[0]?.LobCode) ||
        (previousBusinessKeywordId.current !==
          quote.data.BusinessSecondaryKeyword?.id)
      ) {
        const newLaunchpadId = uuidv4();
        quote.data.LaunchpadId = newLaunchpadId;
        saveQuote.LaunchpadId = newLaunchpadId;
        previousBusinessKeywordId.current =
          quote.data.BusinessSecondaryKeyword?.id || null;
      }
      store.dispatch(updateQuote(saveQuote));
      api.commercialAgentApi.saveQuote(saveQuote, true).then(
        (quoteResponse) => {
          if (quoteResponse.type !== SAVE_QUOTE_RESPONSE) {
            window.scrollTo(0, 0);
            setShowAlert(true);
            setAlertMessage({
              title: "Thank you for your request.",
              message: API_ERROR_MESSAGE,
            });
            hideLoader();
            return;
          } else {
            const redirectUrl = quoteResponse.data.redirectUrl;
            if (saveQuote.SelectedProducts[0]?.LobCode === "CAU") {
              if (!quote.data.CarrierDetail) {
                quote.data.CarrierDetail = [];
              }
              quote.data.CarrierDetail[0] = {
                CarrierCode: quoteResponse.data.carrierCode ? quoteResponse.data.carrierCode : partnerInfo.carrier,
                CarrierName: quoteResponse.data.carrierCode ? quoteResponse.data.carrierCode : partnerInfo.carrier?.toUpperCase(),
                CarrierCob: "Redirect-CAU",
              };
              const updatedQuoteData = {
                ...quote.data,
                CarrierDetail: quote.data.CarrierDetail,
                SelectedProducts: isFirstLob
                  ? cardInfo?.cardInfo.firstLobSelectedProducts ?? []
                  : cardInfo?.cardInfo.secondLobSelectedProducts ?? [],
              };
              store.dispatch(updateQuote(updatedQuoteData));
            }
            window.open(redirectUrl, "_blank");
            hideLoader();
          }
        },
        (error) => {
          setShowAlert(true);
          setAlertMessage({
            title: "Thank you for your request.",
            message: API_ERROR_MESSAGE,
          });
          hideLoader();
        }
      );
    };

    const {
      firstPrimary,
      secondPrimary,
      showSecondInBox,
      firstOthers,
      secondOthers,
    } = getBundlePartnerLayout(
      cardInfo?.cardInfo.firstLobCarriers,
      cardInfo?.cardInfo.secondLobCarriers,
      showProducts
    );

    return (
      <div
        className="agent-partner-product-card-container agent-partner-bundle-card-container"
        style={{
          backgroundColor: showProducts ? backgroundBundleCardColor : "#fff",
          border: showProducts ? "" : ".2rem solid transparent",
          borderColor: mainBundleCardColor,
        }}
      >
        <div className="agent-partner-product-card-top-row-container">
          <div className="agent-partner-product-card-logo">
            {showProducts ? (
              <>
                <span
                  style={{
                    color: mainBundleCardColor,
                  }}
                  {...cardInfo?.cardInfo.firstLobLogoAttributes}
                ></span>
                <>
                  <p
                    className="agent-partner-bundle-plus-icon"
                    style={{
                      color: mainBundleCardColor,
                    }}
                  >
                    +
                  </p>
                  <span
                    style={{
                      color: mainBundleCardColor,
                    }}
                    {...cardInfo?.cardInfo.secondLobLogoAttributes}
                  ></span>
                </>
              </>
            ) : (
              <>
                <div className="agent-partner-product-card-placeholder-logo"></div>
                <>
                  <p
                    className="agent-partner-bundle-plus-icon"
                    style={{
                      color: mainBundleCardColor,
                      marginTop: "12px",
                      marginLeft: "2px",
                      marginRight: "2px",
                    }}
                  >
                    +
                  </p>
                  <div className="agent-partner-product-card-placeholder-logo"></div>
                </>
              </>
            )}
          </div>
          <div className="agent-partner-product-card-title-section">
            <p
              className="agent-partner-product-card-title"
              style={{ color: showProducts ? "" : disabledTextColor }}
            >
              {showProducts ? cardInfo?.lobName : "Line Of Business"}
            </p>
            <div className="agent-partner-select-bundle-card-business-class-section">
              <div
                className="agent-partner-product-card-business-class-div"
                style={{
                  backgroundColor: showProducts ? "#DCDCDC" : mainBundleCardColor,
                  color: "#616161",
                }}
              >
                <p className="agent-partner-product-card-business-class-p">
                  {showProducts
                    ? truncateBusinessClass(
                      30,
                      cardInfo?.cardInfo.firstLobCarriers[0]
                        .carrierBusinessClass
                    ) ?? ""
                    : "TYPE OF BUSINESS"}
                </p>
                {showProducts && (
                  <span
                    className="geico-icon icon-copy geico-icon--informational geico-icon--xsmall copy-button agent-partner-select-primary-copy-icon"
                    data-keyword="copy paste clipboard"
                    onClick={(e) => {
                      e.preventDefault();
                      navigator.clipboard.writeText(
                        cardInfo?.cardInfo.firstLobCarriers[0]
                          .carrierBusinessClass ?? ""
                      );
                    }}
                    title={`Copy "${cardInfo?.cardInfo.firstLobCarriers[0]
                      .carrierBusinessClass ?? ""
                      }" to clipboard`}
                    style={{
                      color: copyIconColor,
                    }}
                  ></span>
                )}
              </div>
              <div
                className="agent-partner-product-card-business-class-div"
                style={{
                  backgroundColor: showProducts ? "#DCDCDC" : mainBundleCardColor,
                  color: "#616161",
                }}
              >
                <p className="agent-partner-product-card-business-class-p">
                  {showProducts
                    ? truncateBusinessClass(
                      30,
                      cardInfo?.cardInfo.secondLobCarriers[0]
                        .carrierBusinessClass
                    ) ?? ""
                    : "TYPE OF BUSINESS"}
                </p>
                {showProducts && (
                  <span
                    className="geico-icon icon-copy geico-icon--informational geico-icon--xsmall copy-button agent-partner-select-primary-copy-icon"
                    data-keyword="copy paste clipboard"
                    onClick={(e) => {
                      e.preventDefault();
                      navigator.clipboard.writeText(
                        cardInfo?.cardInfo.secondLobCarriers[0]
                          .carrierBusinessClass ?? ""
                      );
                    }}
                    title={`Copy "${cardInfo?.cardInfo.secondLobCarriers[0]
                      .carrierBusinessClass ?? ""
                      }" to clipboard`}
                    style={{
                      color: copyIconColor,
                    }}
                  ></span>
                )}
              </div>
            </div>
          </div>
          <div className="agent-partner-product-card-button-section">
            <div className="agent-partner-bundle-card-partner-buttons">
              {showProducts && !isRedirectControl(firstPrimary?.isRedirectable) ? (
                <span
                  className="agent-partner-product-card-button fit-text"
                  style={{ color: mainBundleCardColor, cursor: "default", pointerEvents: "none" }}
                >
                  {firstPrimary.label}
                </span>
              ) : (
              <button
                className="agent-partner-product-card-button fit-text"
                style={{
                  color: showProducts
                    ? mainBundleCardColor
                    : disabledButtonTextColor,
                  backgroundColor: showProducts
                    ? ""
                    : disabledPartnerBackgroundButtonColor,
                  border: showProducts ? "" : "none",
                }}
                onClick={() => {
                  if (firstPrimary) {
                    handleButtonClick(firstPrimary, true);
                  }
                }}
                disabled={!showProducts}
              >
                {showProducts
                  ? firstPrimary?.label
                  : "Partner"}
              </button>
              )}
              {showSecondInBox && (
              <button
                className="agent-partner-product-card-button fit-text"
                style={{
                  color: showProducts
                    ? mainBundleCardColor
                    : disabledButtonTextColor,
                  backgroundColor: showProducts
                    ? ""
                    : disabledPartnerBackgroundButtonColor,
                  border: showProducts ? "" : "none",
                }}
                onClick={() => {
                  if (secondPrimary) {
                    handleButtonClick(secondPrimary, false);
                  }
                }}
                disabled={!showProducts}
              >
                {showProducts
                  ? secondPrimary?.label
                  : "Partner2"}
              </button>
              )}
            </div>
            {(firstOthers.length > 0 || secondOthers.length > 0) && (
                <button
                  onClick={toggleDropdown}
                  className="agent-partner-select-dropdown-toggle"
                  disabled={!showProducts}
                >
                  <div
                    className="agent-partner-select-other-partners-dropdown"
                    style={{ color: showProducts ? "" : disabledTextColor }}
                  >
                    Other Partners{" "}
                    {
                      <span
                        style={{
                          marginLeft: "5px",
                          color: showProducts
                            ? mainProductCardColor
                            : disabledTextColor,
                        }}
                        className={`geico-icon icon-chevron-${isDropdownOpen ? "up" : "down"
                          } geico-icon--small`}
                      ></span>
                    }
                  </div>
                </button>
              )}
          </div>
        </div>

        <div className="agent-partner-products-card-more-options-container">
          {isDropdownOpen && cardInfo?.isEnabled && (
            <>
              <div className="agent-partner-bundle-card-more-options-first-lob-container">
                {firstOthers.map((partnerInfo, index) => (
                    <div
                      key={index}
                      className={
                        isSecondPrimaryPill(index, partnerInfo.isRedirectable)
                          ? "agent-partner-bundle-card-more-options-button"
                          : "agent-partner-bundle-card-more-options-button-secondary"
                      }
                      style={{
                        color: showProducts
                          ? mainBundleCardColor
                          : disabledPartnerBackgroundButtonColor,
                        ...(!isRedirectControl(partnerInfo.isRedirectable) ? { cursor: "default" } : {}),
                      }}
                      onClick={!isRedirectControl(partnerInfo.isRedirectable) ? undefined : () => handleButtonClick(partnerInfo, true)}
                    >
                      <div>
                        <span className="agent-partner-product-card-non-primary-button-text">
                          {`${partnerInfo.label} - ${truncateBusinessClass(
                            80,
                            partnerInfo.carrierBusinessClass
                          )}`}
                        </span>
                        <span
                          className="geico-icon icon-copy geico-icon--informational geico-icon--xsmall copy-button agent-partner-product-card-non-primary-copy-icon"
                          data-keyword="copy paste clipboard"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            navigator.clipboard.writeText(
                              partnerInfo.carrierBusinessClass
                            );
                          }}
                          title={`Copy "${partnerInfo.carrierBusinessClass}" to clipboard`}
                          style={{ color: mainBundleCardColor }}
                          tabIndex={0}
                        ></span>
                      </div>
                    </div>
                  ))}
              </div>
              <div className="agent-partner-bundle-card-more-options-second-lob-container">
                {secondOthers.map((partnerInfo, index) => (
                    <div
                      key={index}
                      className={
                        isSecondPrimaryPill(index, partnerInfo.isRedirectable)
                          ? "agent-partner-bundle-card-more-options-button"
                          : "agent-partner-bundle-card-more-options-button-secondary"
                      }
                      style={{
                        color: showProducts
                          ? mainBundleCardColor
                          : disabledPartnerBackgroundButtonColor,
                        ...(!isRedirectControl(partnerInfo.isRedirectable) ? { cursor: "default" } : {}),
                      }}
                      onClick={!isRedirectControl(partnerInfo.isRedirectable) ? undefined : () => handleButtonClick(partnerInfo, false)}
                    >
                      <div>
                        <span className="agent-partner-product-card-non-primary-button-text">
                          {`${partnerInfo.label} - ${truncateBusinessClass(
                            80,
                            partnerInfo.carrierBusinessClass
                          )}`}
                        </span>
                        <span
                          className="geico-icon icon-copy geico-icon--informational geico-icon--xsmall copy-button agent-partner-product-card-non-primary-copy-icon"
                          data-keyword="copy paste clipboard"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            navigator.clipboard.writeText(
                              partnerInfo.carrierBusinessClass
                            );
                          }}
                          title={`Copy "${partnerInfo.carrierBusinessClass}" to clipboard`}
                          style={{ color: mainBundleCardColor }}
                          tabIndex={0}
                        ></span>
                      </div>
                    </div>
                  ))}
              </div>
            </>
          )}
        </div>
      </div>
    );
  };
